/**
 * Notifications Repository
 *
 * Handles CRUD operations and real-time listeners for user notifications
 * stored at users/{uid}/user_notifications/{id}
 *
 * Replaces the previous localStorage-based notification system with
 * Firestore persistence for cross-device sync.
 */

import {
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  query,
  orderBy,
  limit as fbLimit,
  updateDoc,
  setDoc,
  getDocs,
  where,
  writeBatch,
} from 'firebase/firestore';
import { Paths, withBackoff, db } from './firestoreUtils';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// Types
// ============================================================================

export type FirestoreNotification = {
  id: string;
  title: string;
  message: string;
  timestamp: string; // ISO string for compatibility with existing UI
  read: boolean;
  metadata?: Record<string, any>;
  created_at?: any;
};

type NotificationInput = {
  title: string;
  message: string;
  metadata?: Record<string, any>;
};

/**
 * Per-thread notification badges (2026-09-08): a row that belongs to ONE
 * thread — a routine run, a thread message, anything stamped with a
 * thread_id — is counted by that thread's own badge, not by the account-level
 * bell. The bell's unread count is account-level only.
 */
export function isThreadScopedNotification(n: Pick<FirestoreNotification, 'metadata'>): boolean {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return false;
  if (typeof meta.thread_id === 'string' && meta.thread_id.trim()) return true;
  if (meta.type === 'routine_run') return true;
  if (meta.category === 'threadMessage') return true;
  return false;
}

/** The thread a row points at, when it does (id + optional workspace). */
export function notificationThreadLink(
  n: Pick<FirestoreNotification, 'metadata'>,
): { threadId: string; ws: string | null } | null {
  const meta = n.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const threadId = typeof meta.thread_id === 'string' ? meta.thread_id.trim() : '';
  if (!threadId) return null;
  const ws = typeof meta.account_id === 'string' && meta.account_id.trim()
    ? meta.account_id.trim()
    : typeof meta.ws === 'string' && meta.ws.trim() ? meta.ws.trim() : null;
  return { threadId, ws };
}

/** Account-level unread count — the number the bell shows. */
export function countAccountLevelUnread(notifications: readonly FirestoreNotification[]): number {
  let n = 0;
  for (const item of notifications) {
    if (!item.read && !isThreadScopedNotification(item)) n += 1;
  }
  return n;
}

// ============================================================================
// Real-time Subscription
// ============================================================================

/**
 * Subscribe to real-time notification updates from Firestore.
 * Returns an unsubscribe function.
 */
export function subscribeNotifications(
  uid: string,
  onData: (notifications: FirestoreNotification[]) => void,
  onError?: (error: Error) => void,
  maxItems: number = 100,
): Unsubscribe {
  const col = Paths.userNotificationsCol(uid);
  const q = query(col, orderBy('created_at', 'desc'), fbLimit(maxItems));

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications: FirestoreNotification[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        let timestamp: string;
        if (data.created_at?.toDate) {
          timestamp = data.created_at.toDate().toISOString();
        } else if (data.timestamp) {
          timestamp = data.timestamp;
        } else {
          timestamp = new Date().toISOString();
        }
        return {
          id: doc.id,
          title: data.title || 'notification',
          message: data.message || '',
          timestamp,
          read: data.read === true,
          metadata: data.metadata || undefined,
        };
      });
      onData(notifications);
    },
    (error) => {
      console.error('[notificationsRepository] Snapshot error:', error);
      if (onError) onError(error);
    },
  );
}

// ============================================================================
// Write Operations
// ============================================================================

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  const ref = Paths.userNotificationDoc(uid, notificationId);
  await withBackoff(() => updateDoc(ref, { read: true }));
}

/**
 * Mark all unread notifications as read (batch write).
 */
export async function markAllNotificationsRead(uid: string): Promise<void> {
  const col = Paths.userNotificationsCol(uid);
  const q = query(col, where('read', '==', false));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, { read: true });
  });
  await batch.commit();
}

/**
 * Create a new notification in Firestore.
 */
export async function createNotification(uid: string, data: NotificationInput): Promise<string> {
  const id = uuidv4();
  const ref = Paths.userNotificationDoc(uid, id);
  await withBackoff(() =>
    setDoc(ref, {
      title: data.title,
      message: data.message,
      created_at: serverTimestamp(),
      read: false,
      metadata: data.metadata || null,
    }),
  );
  return id;
}

// ============================================================================
// Migration: localStorage → Firestore (one-time)
// ============================================================================

const MIGRATION_FLAG_PREFIX = 'notifications_migrated_to_firestore_';

/**
 * Migrate existing localStorage notifications to Firestore.
 * Runs once per account, then sets a flag so it won't run again.
 */
export async function migrateLocalStorageNotifications(uid: string, accountId: string): Promise<void> {
  const migrationKey = `${MIGRATION_FLAG_PREFIX}${accountId}`;
  if (localStorage.getItem(migrationKey)) return;

  try {
    const raw = localStorage.getItem(`notifications_${accountId}`);
    if (!raw) {
      localStorage.setItem(migrationKey, '1');
      return;
    }

    const items: Array<{ id: string; title: string; message: string; timestamp: string }> = JSON.parse(raw);
    if (!Array.isArray(items) || items.length === 0) {
      localStorage.setItem(migrationKey, '1');
      return;
    }

    const readRaw = localStorage.getItem(`notifications_read_${accountId}`);
    const readIds: string[] = readRaw ? JSON.parse(readRaw) : [];

    // Write each notification to Firestore
    const batch = writeBatch(db);
    for (const item of items.slice(0, 100)) {
      const ref = Paths.userNotificationDoc(uid, item.id || uuidv4());
      batch.set(ref, {
        title: item.title || 'notification',
        message: item.message || '',
        created_at: item.timestamp ? new Date(item.timestamp) : new Date(),
        read: readIds.includes(item.id),
        metadata: null,
      });
    }
    await batch.commit();

    // Clear localStorage keys
    localStorage.removeItem(`notifications_${accountId}`);
    localStorage.removeItem(`notifications_read_${accountId}`);
    localStorage.setItem(migrationKey, '1');
  } catch (err) {
    console.warn('[notificationsRepository] Migration failed:', err);
  }
}
